import { Link } from 'react-router-dom';

/**
 * The user manual.
 *
 * Public: main.jsx routes this outside the sign-in gate, beside privacy and
 * terms, for the same reason those are public — somebody deciding whether to
 * sign up, and an app store reviewer, both need to read what the thing does
 * without making an account first.
 *
 * The rule for editing this file is the one the legal pages already follow:
 * everything here describes what the app does TODAY, and every claim was read
 * out of web/src/pages and server/src before it was written down. A manual that
 * documents an intention is worse than no manual, because it is believed.
 *
 * Numbers that live in code are stated here in prose, which means they can
 * drift. The ones worth watching: the plan limits and the trial (PLAN_LIMITS,
 * TRIAL_DAYS, TRIAL_LIMIT in services/metering.js), the stale window
 * (STALE_AFTER_DAYS in routes/index.js), the expiry window (expiringItems' 7
 * days), the match thresholds (AUTO_ACCEPT / SUGGEST in services/matching.js),
 * the invite lifetime (INVITE_DAYS in services/members.js), and the referral
 * reward and the window for entering a code (REWARD_SCANS / REDEEM_DAYS in
 * services/refer.js).
 */

const SECTIONS = [
  ['getting-started', 'Getting started'],
  ['the-pantry', 'The pantry'],
  ['scanning', 'Scanning things in'],
  ['items-and-matching', 'Items and matching'],
  ['recipes', 'Recipes'],
  ['cant-tell', '“Can’t tell” is an answer'],
  ['shopping-and-planning', 'Shopping list and meal plan'],
  ['household', 'Your household'],
  ['plans', 'Plans and the scan allowance'],
  ['your-data', 'Your data'],
  ['appearance', 'Appearance'],
  ['troubleshooting', 'When something goes wrong'],
];

export default function Manual() {
  return (
    <div className="legal">
      <article className="legal-body">
        {/* Above the title, because this page renders outside the app shell:
            an installed PWA has no browser back button, and twelve sections is
            a long way to scroll for the footer's Home link. */}
        <p style={{ margin: '0 0 14px' }}>
          <Link to="/">&larr; Back to Pantry</Link>
        </p>
        <h1>User manual</h1>
        <p className="legal-meta">Pantry Pro &middot; Settings &rarr; Help</p>

        <p>
          Pantry Pro keeps an inventory of the food in your home, and uses it to answer two
          questions: what can I cook tonight, and what do I actually need to buy. This page
          describes what the app does now. It lives in the app under{' '}
          <strong>Settings &rarr; Help &rarr; User manual</strong>, and at{' '}
          <strong>/help</strong>, which works without an account.
        </p>

        <ul>
          {SECTIONS.map(([id, title]) => (
            <li key={id}><a href={`#${id}`}>{title}</a></li>
          ))}
        </ul>

        {/* ---------------------------------------------------------------- */}

        <h2 id="getting-started">Getting started</h2>
        <p>
          You sign in with Google or Apple, or with your email address &mdash; which of those
          you are offered depends on how the deployment is configured. There is no password to
          invent, and none ever reaches this app.
        </p>
        <p>
          Signing in by email sends one message holding a <strong>numeric code</strong> and a
          link. Type the code into the box the app is already showing and the sign-in finishes
          there. That is the half that matters on a phone: the link opens in a browser rather
          than in the installed app, so it signs you in beside the app instead of in it. On a
          laptop, where the browser is the app, the link is one tap and does the same job.
          Either works once, and expires.
        </p>
        <p>
          Signing in does not create a pantry: having an account and owning a pantry are separate
          things, and the second person in a household is usually joining one that already
          exists rather than starting another. So the screen after sign-in offers three things:
        </p>
        <ul>
          <li>
            <strong>An invitation waiting for you.</strong> If somebody invited the address you
            signed in with, their pantry is offered by name. It is offered, never taken
            automatically.
          </li>
          <li>
            <strong>An invitation code.</strong> If somebody read you a code rather than sending
            a link, type it here.
          </li>
          <li>
            <strong>Your own pantry.</strong> Name it or leave it blank, and it is named after
            you.
          </li>
        </ul>
        <p>
          A new pantry arrives furnished: six storage locations, a few stores, and the curated
          list of recipe sites. It does not arrive with sample groceries in it &mdash; food you
          did not buy is data you would have to delete before the app could tell you the truth
          about what you own.
        </p>
        <p>
          <strong>The tour.</strong> A short introduction runs the first time you sign in, and
          you can skip it. Seven steps &mdash; getting food in, adding recipes, cooking from
          what you have, planning and shopping, and sharing the household &mdash; each with a
          button into the real screen, and steps check themselves off from what is actually in
          your pantry. It is remembered per person rather than per pantry, so somebody who
          joins your household later gets their own, and finishing or skipping it stops it
          opening again. To see it a second time, use{' '}
          <strong>Settings &rarr; Help &rarr; Take the tour again</strong>.
        </p>

        {/* ---------------------------------------------------------------- */}

        <h2 id="the-pantry">The pantry</h2>
        <p>
          <strong>Storage locations.</strong> Six are ready made: Kitchen Refrigerator, Kitchen
          Freezer, Pantry, Spice Drawer, Garage Refrigerator and Deep Freezer. Rename them, add
          your own, or reorder them under <strong>Settings &rarr; Storage locations</strong>.
          Each one has a kind &mdash; refrigerator, freezer, pantry, cabinet, spice, cellar or
          other &mdash; which is a label rather than a rule.
        </p>
        <p>
          Removing a location that still holds food archives it instead of deleting it: the
          items stay exactly where they are and the location stops appearing in pickers. An
          archived location can be restored from the same screen. An empty one is deleted
          outright.
        </p>
        <p>
          <strong>Items and quantities.</strong> Every row in the pantry is a quantity and a unit
          in one location, optionally with a purchase date (<em>Purchased</em>), an expiry date
          (<em>Expires</em>) and a note (<em>Notes</em> &mdash; &ldquo;opened, half used&rdquo;).
          Adding a row by hand asks for the item, the quantity, the unit, the location and the
          purchase date; the expiry date and the note are set on the row afterwards. Tapping a row
          opens it, and the quick actions there are the ones that
          come up in a kitchen: <em>Used 1</em>, <em>Used it all</em>, <em>Still here</em> and{' '}
          <em>Threw out</em>.
        </p>
        <p>
          <strong>Pictures.</strong> Every row carries a small drawing of the food, picked from
          the item&rsquo;s name and its category, so a long list can be found by shape rather
          than read line by line. An item added by scanning a barcode shows the product&rsquo;s
          own photograph instead. Those come from Open Food Facts, and only the address is kept
          here &mdash; each one loads from that site whenever a list is shown.
        </p>
        <p>
          <strong>Use these soon.</strong> The home screen lists anything with an expiry date
          that has already passed or falls within the next seven days. Rows you have used up do
          not appear &mdash; &ldquo;use it up&rdquo; is not advice anybody can follow about nothing.
        </p>
        <p>
          <strong>Not checked in a while.</strong> This is the part that keeps the inventory
          honest. Nothing here can see your shelves, so a row that says two jars of something has
          only ever been as true as the last time a person looked. <em>Still here</em> stamps a
          row as checked, and the home screen lists whatever has gone thirty days without being
          verified, oldest first. The row&rsquo;s own sheet is gentler and says it is worth a look
          after a two weeks.
        </p>
        <p>
          <strong>Running low</strong> lists staples whose total on hand has fallen to or below
          the threshold set on the item. Only items marked as staples appear here, because a
          staple is the kind of thing you restock rather than plan around.
        </p>
        <p>
          <strong>Recent activity</strong> is the last fifteen changes to the inventory, newest
          first. Each line begins with what happened &mdash; <em>add</em>, <em>consume</em>,{' '}
          <em>adjust</em>, <em>move</em>, <em>verify</em>, <em>discard</em> or <em>delete</em>{' '}
          &mdash; then the item, how long ago, by how much, and the name of whoever did it: the
          one they set under <strong>Settings &rarr; Household</strong>, or their email address
          until they set one. A change that came out of a receipt or a shelf photo is marked{' '}
          <em>via receipt</em> or <em>via vision</em>; one the app made on its own carries no
          name.
        </p>

        {/* ---------------------------------------------------------------- */}

        <h2 id="scanning">Scanning things in</h2>
        <p>
          Five ways in, on the <strong>Scan</strong> tab and in{' '}
          <strong>Settings &rarr; Import purchase history</strong>. The first rule for all of
          them: reading a photograph or an email is good and not perfect, so nothing lands in
          your pantry without a review step.
        </p>

        <h3>A receipt</h3>
        <p>
          Photograph the receipt and it is read into a <em>draft</em> purchase, which opens as a
          review screen. Every line shows what was printed and what it was matched to. From
          there you can tap a line to match it to something you already have, correct the
          quantity or unit, or exclude it entirely. Lines that matched nothing are marked{' '}
          <em>new</em>, and will create items when you confirm.
        </p>
        <p>
          Confirming does three things in one go: unmatched lines become new items, every line
          teaches that store&rsquo;s shorthand, and &mdash; if you leave the box checked &mdash; the
          food lines are added to your inventory, dated by the receipt&rsquo;s own purchase date
          rather than today. You choose where they go, or let each item use its default location.
        </p>
        <p>
          <em>Re-read the photo</em> runs the extraction again. It replaces every line, including
          matches and exclusions you made by hand, and it spends another scan &mdash; so it asks
          first.
        </p>

        <h3>A shelf</h3>
        <p>
          Open the door, take one clear photo. Choosing the location first is optional but worth
          it: only then can the scan also flag things the pantry expected to see. The review
          splits into three lists.
        </p>
        <ul>
          <li><strong>New to inventory</strong> &mdash; found in the photo, not currently tracked here.</li>
          <li><strong>Already tracked</strong> &mdash; found, and matched to a row you already have.</li>
          <li>
            <strong>Expected but not visible</strong> &mdash; tracked in this location, and the
            photo does not show it. Removing these is opt-in and off by default, because a photo
            missing something is weak evidence: it may simply be behind something.
          </li>
        </ul>
        <p>
          Rows that came out of the photo carry how confident the reading was. Any row can be
          rematched or skipped before you apply, and a location has to be chosen before the scan
          can be applied at all.
        </p>

        <h3>A barcode</h3>
        <p>
          Unlimited, on every plan, and no model is involved at any point. The camera decodes the
          barcode on your own device &mdash; no photo is uploaded and none is kept &mdash; and the
          number is looked up: first against your own items, then USDA FoodData Central and Open
          Food Facts. You can also type the number underneath the barcode into the box on the same
          screen and press <em>Look up</em>, which is the answer to a curved jar the camera will
          not focus on.
        </p>
        <p>
          If no database recognizes it, name it once. The barcode is stored on the item, so the
          next scan of the same product is an exact local hit rather than another lookup.
        </p>

        <h3>Purchase history, as a CSV</h3>
        <p>
          Under <strong>Settings &rarr; Import purchase history</strong>. The recognized columns
          are <strong>date</strong>, <strong>item</strong> (or description, product, name),{' '}
          <strong>quantity</strong>, <strong>unit</strong>, <strong>unit price</strong>,{' '}
          <strong>total</strong> and <strong>order id</strong>; common variants of each header
          are accepted, and extra columns are ignored. Only the item column is required. Rows are
          grouped into one purchase per order id, or per date when there is no order id.
        </p>
        <p>
          Re-importing an overlapping range is safe &mdash; nothing is duplicated. Imported
          purchases fill in your item list and your purchase history, and deliberately do{' '}
          <em>not</em> add to the current inventory: groceries bought three months ago have
          already been eaten, and treating them as on hand would corrupt every recipe check.
        </p>

        <h3>Receipts by email</h3>
        <p>
          Under <strong>Settings &rarr; Import purchase history</strong>, where this deployment
          offers it, your household has an email address of its own. Forward a store&rsquo;s
          order confirmation, a digital receipt from a store&rsquo;s app, or a photograph of a
          paper one attached to a blank email, and it appears under <em>Receipts to review</em> on
          the Scan tab within a couple of minutes, with the sender and subject in its notes. It is
          read the same way a photographed receipt is and reviewed on the same screen.
        </p>
        <p>
          An image or PDF attached to the email is the receipt; with no attachment, the text of
          the email is. Each one costs one scan, exactly as a photograph does. If your allowance
          is spent, the card in Settings says so and the email stays in your mailbox to forward
          again after the reset. Anyone who knows the address can put a draft in front of you to
          review, and nothing more &mdash; the address is yours to rotate if it gets around.
        </p>

        <h3>What spends the allowance</h3>
        <p>
          Only work that a model has to do is counted, and there are four kinds of it: reading a{' '}
          <strong>receipt</strong> &mdash; photographed or emailed &mdash; a{' '}
          <strong>shelf photo</strong>, a <strong>recipe photo</strong>, and{' '}
          <strong>recipe text</strong> &mdash; which happens when you paste a recipe in, or import
          a link whose page publishes no recipe data of its own. The app calls all four
          &ldquo;photo scans&rdquo;.
        </p>
        <p>
          Everything else is unlimited on every plan: barcode scanning, CSV import, typing things
          in by hand, browsing, searching your own recipes, ranking recipes against the pantry,
          shopping lists and meal plans. Searching the cooking sites does not spend a scan either;
          it uses the deployment&rsquo;s search provider, which has limits of its own.
        </p>

        {/* ---------------------------------------------------------------- */}

        <h2 id="items-and-matching">Items and matching</h2>
        <p>
          Underneath everything is one list with one row per real-world food &mdash; the{' '}
          <strong>known items</strong> count on the home screen. Inventory rows, receipt lines,
          shelf-scan results, recipe ingredients and barcode scans all funnel to it. That is why
          a recipe asking for whole milk can be answered by a receipt line that said{' '}
          <span className="mono">GV WHL MLK GAL</span>: both of them point at the same item.
        </p>
        <p>Text becomes an item in this order, cheapest and surest first:</p>
        <ul>
          <li><strong>Barcode</strong>, if there is one. Exact.</li>
          <li>
            <strong>Learned shorthand.</strong> Wording somebody has already confirmed, preferring
            what was confirmed at the same store &mdash; the same abbreviation can mean different
            products at different chains.
          </li>
          <li><strong>The item&rsquo;s own name</strong>, matched exactly once punctuation and case are set aside.</li>
          <li>
            <strong>A similarity score.</strong> A strong score is applied on its own. A middling
            one is <em>offered</em> and not applied, which is why you sometimes see chips of close
            matches instead of a decision. A weak one is ignored.
          </li>
        </ul>
        <p>
          <strong>When a match is wrong or missing,</strong> the correction is the same everywhere
          and it is worth making, because it is remembered:
        </p>
        <ul>
          <li>On a receipt line or a shelf-scan row, tap it and pick the right item, or tap one of the <em>close matches</em> chips.</li>
          <li>On a recipe ingredient, tap a suggestion chip, or the <em>match&hellip;</em> button beside the line.</li>
        </ul>
        <p>
          Confirming stores that wording against the item, so the next receipt, import or scan
          using it matches on its own. A correction made by a person also overrules a shorthand
          that pointed somewhere else &mdash; otherwise the tap that fixed it would have
          reinforced the mistake. The wordings learned for an item are listed on the item&rsquo;s
          own page under <strong>Receipt shorthand</strong>, and any of them can be removed.
        </p>
        <p>
          Matching a recipe ingredient links only the ingredient to the item. It does not change
          the recipe&rsquo;s wording or its quantities, which belong to the recipe.
        </p>
        <p>
          <strong>What an item carries.</strong> Open one under <strong>Master items</strong> and press{' '}
          <em>Edit</em>: besides its name, brand, category and unit, an item holds the handful of
          fields the rest of the app reads.
        </p>
        <ul>
          <li>
            <strong>Usually kept in</strong> &mdash; where this food normally lives. Confirming a
            receipt without picking one location for the whole purchase sends each item to its
            own.
          </li>
          <li>
            <strong>Shelf life (days)</strong> &mdash; how long it keeps from the day it is
            bought. It warns nobody by itself; what it does is fill in the expiry date when a row
            is added without one, which is what puts the food into <em>Use these soon</em> at the
            right time. Counted from the purchase date on a receipt or a row you add by hand, and
            from today on a shelf photo, which shows that something is there rather than when it
            arrived. A date you enter yourself is never overwritten, and an item with no shelf
            life simply gets no date.
          </li>
          <li>
            <strong>Low stock at</strong> &mdash; the threshold behind <em>Running low</em> and{' '}
            <em>Restock staples</em>. Left empty, a staple counts as low only when there is none
            of it left.
          </li>
          <li>
            <strong>Package size</strong> and <strong>Package unit</strong> &mdash; what one of
            them holds: 14 and fl oz, for a can of broth. This is the field that answers a
            can&rsquo;t-tell; what happens without one is under{' '}
            <a href="#cant-tell">&ldquo;Can&rsquo;t tell&rdquo; is an answer</a>, below.
          </li>
          <li>
            <strong>Staple</strong> &mdash; &ldquo;track as present, not measured&rdquo;. Only
            staples appear in <em>Running low</em>, and a recipe calling for one is answered on
            presence alone rather than on the amount.
          </li>
          <li>
            <strong>UPC</strong> &mdash; the barcode. Filled in for you the first time you scan a
            product this pantry has not seen, whether a database named it or you did, and
            typeable by hand for a label that will not scan.
          </li>
        </ul>

        {/* ---------------------------------------------------------------- */}

        <h2 id="recipes">Recipes</h2>

        <h3>Getting a recipe in</h3>
        <ul>
          <li>
            <strong>From a link.</strong> The importer reads the recipe data the site publishes
            for machines &mdash; the same data Google reads for search results. That is accurate,
            free, and stable across redesigns. If a page publishes none, the model reads the
            page text instead, and that spends one scan.
          </li>
          <li>
            <strong>Pasted.</strong> Some publishers refuse server-side fetches outright. Select
            the recipe in your own browser, copy it, and paste it in; the app offers this box
            automatically when a fetch is refused. If what you paste happens to be page source,
            the markup reader gets first crack at it, which is both free and more accurate.
          </li>
          <li>
            <strong>From a photo.</strong> A cookbook page, an index card, handwriting, or a
            screenshot of a recipe somebody sent you. Select several photos for a multi-page
            recipe.
          </li>
          <li><strong>Typed in</strong>, if it is a family recipe with no page to point at.</li>
        </ul>

        <h3>Finding one</h3>
        <p>
          <strong>Recipes &rarr; Find a new recipe online</strong> searches only the sites on your
          own source list &mdash; Food Network, Southern Living, Serious Eats, AllRecipes, Bon
          App&eacute;tit and NYT Cooking to begin with. Add sites, or switch any of them off,
          under <strong>Settings &rarr; Recipe sources</strong>. Searching happens when you press
          the button rather than as you type. Importing a result goes through exactly the same
          link importer as a link you pasted yourself. A result that turns out to be a list &mdash;
          &ldquo;65 easy sheet pan recipes&rdquo; &mdash; offers <em>Show the recipes</em>, which
          opens it up into the recipes it names as cards you can add one at a time.
        </p>

        <h3>Cooking from what you have</h3>
        <p>
          <strong>Cook now</strong> ranks the recipes you have saved against the pantry as it
          stands. The picker at the top narrows it to recipes that call for particular things you
          own &mdash; up to three, and it suggests whatever is expiring within the week. Two more
          filters: how many missing ingredients you will tolerate, and a time limit.
        </p>
        <p>
          The ranking puts fewest missing-or-short ingredients first, then fewest it could not
          judge, then best coverage. Each card names what you would have to buy, and lists
          separately anything it could not verify.
        </p>
        <p>
          <em>Search the web with these</em> extends the same idea past your own recipe box: it
          reads the published recipe data on pages from your source list and scores their
          ingredients against your pantry. Nothing is saved and no model is asked &mdash; a page
          nobody chose to import should not fill up the recipe box or cost anything. Pages that
          would not load, or that carry no recipe data, are still listed underneath with the
          reason, because they are still links worth opening by hand.
        </p>

        <h3>Servings</h3>
        <p>
          A recipe with a stated yield can be scaled to half, as written, or double. Every
          quantity on the page is restated at that scale, the pantry check re-runs against the
          scaled amounts, and adding to the shopping list uses them too.
        </p>
        <p>
          <em>Cooked it</em> records that you made the recipe and counts it. It does not deduct
          the ingredients from your inventory; what you actually used is for you to adjust.
        </p>

        {/* ---------------------------------------------------------------- */}

        <h2 id="cant-tell">&ldquo;Can&rsquo;t tell&rdquo; is an answer</h2>
        <p>
          Every required ingredient in a recipe gets one of four verdicts, and the fourth is the
          one that makes the other three trustworthy.
        </p>
        <ul>
          <li><strong>have it</strong> &mdash; enough of it is on the shelf.</li>
          <li><strong>not enough</strong> &mdash; some is there, and less than the recipe asks for.</li>
          <li><strong>need</strong> &mdash; none is on hand, or the line matched nothing you own.</li>
          <li><strong>can&rsquo;t tell</strong> &mdash; the question could not be answered honestly.</li>
        </ul>
        <p>
          The usual cause is units that do not reconcile. A recipe wants <strong>4 cups</strong>{' '}
          of broth; the pantry holds <strong>2 cans</strong>. The cans are real and the amount
          is real, and nothing here has been told how many cups are in that can. Calling it{' '}
          <em>have it</em> would send you to the stove short, and calling it <em>need</em> would
          put broth you already own on a shopping list. So it says it cannot tell, and shows you
          what it did find instead &mdash; how many cans, which location they are in, and that
          they cannot be converted to cups.
        </p>
        <p>
          <strong>The fix is on the item, not the recipe.</strong> Record what one of them holds
          &mdash; the item&rsquo;s <em>Unit</em> set to the can, <em>Package size</em> to 14 and{' '}
          <em>Package unit</em> to fl oz &mdash; and cans convert to cups from then on, for this
          recipe and every other one that asks by volume. A package size given by weight (oz, lb)
          reconciles with a recipe&rsquo;s cups only for the foods the app knows a weight per cup
          for, and always with a recipe that asks by weight. An item created by a barcode scan
          often arrives with the size already filled in from the catalog.
        </p>
        <p>
          Two deliberate exceptions keep this from being pedantic: staples (salt, pepper, flour)
          are tracked as present rather than measured, and a line with no amount at all
          (&ldquo;salt to taste&rdquo;) needs only presence. Both read as <em>have it</em>.
        </p>
        <p>
          A recipe is only marked ready when nothing is missing, short, <em>or</em> unresolved. In
          Cook now, a recipe whose only gaps are can&rsquo;t-tells is labeled unclear rather than
          ready, and the uncertain lines are listed apart from the ones to buy.
        </p>
        <p>
          <strong>Shopping lists leave can&rsquo;t-tells off by default, and say how many they left
          off.</strong> The checkbox on the same dialog adds them anyway; those lines carry a note
          saying stock could not be verified, so you can check the cabinet in the store rather
          than buy blind.
        </p>

        {/* ---------------------------------------------------------------- */}

        <h2 id="shopping-and-planning">Shopping list and meal plan</h2>
        <p>
          There is one active list, and it fills from three places: what a recipe needs and you
          do not have, staples that have run low, and anything you add by hand.
        </p>
        <ul>
          <li>
            <strong>From a recipe.</strong> A missing ingredient is added at the whole amount the
            recipe asks for; one you are merely short of is added at the shortfall only. Things
            you already have are skipped, and counted in the summary so you can see they were
            considered.
          </li>
          <li>
            <strong>Restock staples</strong> adds every staple that has fallen to or below its
            threshold, at the threshold quantity &mdash; or one, where no threshold is set.
          </li>
          <li>
            <strong>By hand</strong>, either something you have bought before or a name typed in.
          </li>
        </ul>
        <p>
          Adding the same thing twice merges it into the line already there rather than making a
          second one. When the units cannot be reconciled &mdash; 2 cans and 14 oz &mdash; the
          line keeps a note (&ldquo;also: 14 oz&rdquo;) instead of inventing a combined number.
        </p>
        <p>
          The list is grouped by aisle rather than alphabetically, so you walk the store once.
          Checking something off moves it to the bottom of its group with a line through it;{' '}
          <em>Clear N bought</em> removes them when you are done. <em>Copy as text</em> gives you
          a plain checklist to paste anywhere.
        </p>
        <p>
          <strong>The meal plan</strong> is a week of breakfast, lunch and dinner slots. A slot
          takes a saved recipe or just a line of text (&ldquo;leftovers&rdquo;), and the picker
          leads with the recipes closest to cookable with what you already have.{' '}
          <em>Build the week&rsquo;s shopping list</em> checks every
          planned recipe against the pantry in one pass, adds the gaps to the list, tells you how
          many ingredients you already had, and names the ones it could not check. Those get their
          own <em>Add these too</em> button, which adds only them &mdash; asking for everything a
          second time would double the amounts already on the list.
        </p>

        {/* ---------------------------------------------------------------- */}

        <h2 id="household">Your household</h2>
        <p>
          A pantry is shared by construction. Everyone in a household sees one inventory, one
          item list, one shopping list and one meal plan, and activity is attributed by name
          &mdash; which is why you can change the name you appear under in{' '}
          <strong>Settings &rarr; Household</strong>.
        </p>
        <p>
          <strong>Inviting somebody.</strong> An owner creates an invitation, which produces both
          a link and a short code meant to be read aloud (it leaves out the characters people
          confuse out loud &mdash; no O or 0, no I, 1 or L). Give an email address and, where the
          deployment can send email, the invitation is sent there as well. You are told which
          happened on the same screen that shows you the link, and a message that was refused says
          so there rather than leaving you to wonder. Whether this deployment can send at all is
          under <strong>Settings &rarr; Status</strong>, beside <em>Invitation email</em>, as{' '}
          <em>can send</em> or <em>not configured</em>.
        </p>
        <p>
          None of that changes the invitation itself: the link and the code <em>are</em> the
          invitation, and sending them by hand always works. An address is worth giving even where
          no mail can go out &mdash; if that person signs up with the same one, the invitation is
          offered to them without any code at all.
        </p>
        <p>
          An invitation works <strong>once</strong> and expires after <strong>seven days</strong>.
          The link is shown exactly once, at the moment it is made: it is stored as a hash and
          cannot be read back, so if it gets lost, make another. Owners can revoke one that has
          not been used.
        </p>
        <p>
          <strong>Accepting moves you.</strong> One account belongs to one household, so joining
          another takes you out of the one you are in. An empty starter household is cleaned up on
          the way out. One that still holds food, receipts or recipes refuses the move instead of
          quietly deleting it &mdash; export it or delete it first, from Settings.
        </p>
        <p>
          <strong>Roles.</strong> An owner can invite, revoke, promote, demote and remove people.
          A member can do everything else in the app, and the only person a member can remove is
          themselves. The last owner cannot leave or be demoted: promote somebody else first, or
          delete the household outright under <strong>Your data</strong>. Leaving means losing
          access to everything in that household; the record of what somebody did stays behind
          with their name on it.
        </p>

        {/* ---------------------------------------------------------------- */}

        <h2 id="plans">Plans and the scan allowance</h2>
        <p>Three plans, and only the model reads are capped on any of them.</p>
        <ul>
          <li><strong>Free</strong> &mdash; 5 photo scans a month.</li>
          <li>
            <strong>Trial</strong> &mdash; 20 a month for 14 days. A card is required, it becomes
            the paid plan when the trial ends unless you cancel first, and a household gets one
            trial ever.
          </li>
          <li><strong>Paid</strong> &mdash; 40 a month, at $5 a month or $50 a year.</li>
        </ul>
        <p>
          <strong>The month is yours, not the calendar&rsquo;s.</strong> It runs from the day of
          the month you signed up to the same day of the next month, and the count returns to
          zero at midnight UTC on the date Settings shows beside <em>Resets</em>.
        </p>
        <p>
          <strong>Everything that is not one of those four model reads is unlimited on every
          plan</strong> &mdash; inventory, barcode scanning, CSV imports, recipes you already
          have, ranking, shopping lists, meal plans, all of it. The cap exists because those reads
          are the only part of the app that costs money per use.
        </p>
        <p>
          A banner at the top of the Scan tab says how many are left before it matters. When they
          are gone, the receipt and shelf buttons are disabled rather than letting you frame a
          shot and be refused afterwards, and the banner says the date they come back. Nothing
          else changes.
        </p>
        <p>
          The trial is offered on that same banner, once the free allowance is nearly or fully
          spent, and only to a household that has not had one. Settings offers the subscription
          itself at any time. If you installed the app from Google Play, it offers neither:
          plans are started and managed at pantry-pro.app in a web browser, and the app itself
          sells nothing. Everything you subscribe to there works in the Play app the moment it
          is paid for.
        </p>
        <p>
          <strong>Canceling</strong> happens on Stripe&rsquo;s own screens, reached from{' '}
          <em>Manage subscription</em> in Settings on the website, along with changing a card
          and finding old invoices. The paid allowance runs to the end of the period you have already paid for,
          and then falls back to free. <strong>The pantry is never locked</strong> &mdash; nothing
          is deleted, hidden or made read-only when a subscription ends. If a renewal fails, the
          paid allowance stays while the payment is retried, and Settings says so.
        </p>
        <p>
          Payment is handled entirely by Stripe. No card details reach this app. If a deployment
          has no billing configured at all, Settings says that too, and the free allowance
          applies.
        </p>
        <p>
          <strong>Advertising.</strong> Where a deployment has advertising configured, the free
          plan carries it, on the recipe and meal-planning screens and nowhere else &mdash; never
          on the shopping list. Paid plans and trials have none, and on those no advertising
          script is loaded at all. Where a deployment has none configured, there is no advertising
          anywhere in the app.
        </p>

        <h3>Refer a friend</h3>
        <p>
          <strong>Settings &rarr; Refer a friend</strong> gives you two forms of the same thing: a
          link to send, and a short code to read out. When somebody who used yours starts a pantry
          of their own <em>and scans something with it</em>, both households get{' '}
          <strong>20 photo scans</strong>. It is paid on use rather than on signup, so the person
          you told has to have actually tried the thing.
        </p>
        <p>
          Those scans are not part of the monthly allowance and do not reset with it. They sit
          beside it, they stay until they are used, and they are only spent once the month&rsquo;s
          allowance is gone. While there are any, the Plan section gives them a line of their own
          &mdash; <em>Referral scans</em>, and how many are left. The referral screen shows how many
          pantries started with your code and how many of those have actually been used, which are
          deliberately two different numbers. It never shows who they are.
        </p>
        <p>
          Going the other way, a pantry can enter somebody&rsquo;s code in its first{' '}
          <strong>30 days</strong>, once ever, and not its own household&rsquo;s or that of a
          pantry you are already in. If you arrived by a friend&rsquo;s link the box is already
          filled in. It is a thank-you to whoever told you about the app rather than a discount
          &mdash; nothing about your plan, your price or your renewal changes &mdash; and unlike
          subscribing, both halves of it work in the app installed from Google Play.
        </p>
        <p>
          This is not the household invitation described above. An invitation lets another person
          into <em>your</em> pantry, and everything in it is then shared. A referral is a
          recommendation to somebody who will keep a pantry of their own, and the two households
          never see anything of each other&rsquo;s.
        </p>

        {/* ---------------------------------------------------------------- */}

        <h2 id="your-data">Your data</h2>
        <p>All three of these are in <strong>Settings &rarr; Your data</strong>.</p>
        <ul>
          <li>
            <strong>Download everything.</strong> One file containing every row your household
            holds &mdash; inventory, purchases and receipts, recipes, lists, plans &mdash; plus who
            can reach it and where its photographs are stored.
          </li>
          <li>
            <strong>Delete everything.</strong> Removes the household and every row in it, deletes
            the stored photographs, and cancels any subscription. You confirm by typing DELETE. It
            cannot be undone and no copy is kept, so take the download first if you want one.
          </li>
          <li>
            <strong>Do not sell or share my personal information.</strong> Nothing is ever sold.
            Where a deployment shows advertising, opting out makes the advertising you see
            non-personalized: it does not remove the advertising, and nothing else about the app
            changes, because charging somebody in features or in money for exercising a right is
            the one thing the law plainly forbids. Where a deployment shows none, there is nothing
            for the opt-out to stop, and it is recorded anyway so that it already applies if that
            ever changes. A Global Privacy Control signal from your browser is honored
            automatically, either way.
          </li>
        </ul>
        <p>
          Deleting one receipt or one shelf scan takes its photograph with it. Deleting
          everything, above, deletes every stored photograph the household has. The full account
          of what is collected, who else is involved, and what is never done with it is in the{' '}
          <Link to="/privacy">privacy policy</Link>; the{' '}
          <Link to="/terms">terms of use</Link> sit beside it.
        </p>

        {/* ---------------------------------------------------------------- */}

        <h2 id="appearance">Appearance</h2>
        <p>
          <strong>Settings &rarr; Appearance</strong> holds two choices, and neither of them
          changes what the app does. The first is <em>Match device</em>, <em>Light</em> or{' '}
          <em>Dark</em>. Match device follows whatever the phone or laptop is set to, and changes
          when that does.
        </p>
        <p>The second is which of four looks the app wears:</p>
        <ul>
          <li><strong>Pantry</strong> &mdash; the original. Deep green, system type, soft edges.</li>
          <li>
            <strong>Market</strong> &mdash; persimmon on warm paper. Heavy type, round corners,
            photos to the edge.
          </li>
          <li>
            <strong>Homestead</strong> &mdash; cream and sage. Serif headings, and photos matted
            like a cookbook plate.
          </li>
          <li>
            <strong>Broadsheet</strong> &mdash; newsprint. Square corners, hairline rules, black
            doing the work of color.
          </li>
        </ul>
        <p>
          Each is listed beside a swatch painted in that theme&rsquo;s own colors and corners
          rather than a picture of them, so what the list shows cannot drift from what the app
          will look like. Every screen changes the moment you choose one.
        </p>
        <p>
          The choice is kept by the browser, on that device only. It is not part of your account:
          a second phone starts again at Pantry and Match device, two people in one household can
          each have their own, and clearing your browser&rsquo;s site data forgets it. The{' '}
          <a href="/guides/">guides</a> are part of the same site and read the same setting, so an
          article opens in the colors and shapes of the theme you chose in the app; the
          typefaces Market, Homestead and Broadsheet use are loaded by the app itself and do
          not follow.
        </p>

        {/* ---------------------------------------------------------------- */}

        <h2 id="troubleshooting">When something goes wrong</h2>

        <h3>A site that will not import</h3>
        <p>
          Some publishers refuse anything that is not a live browser, by a fingerprint no
          combination of headers gets past. The app tells you which site refused and opens a paste
          box: select the recipe in your own browser, copy it, and paste it in. Photographing the
          page works too. This is not a failure you can retry your way out of, which is why the
          alternative is offered rather than a <em>try again</em> button.
        </p>
        <p>
          A different message means something else: if the page is a roundup &mdash; &ldquo;64 best
          chicken thigh recipes&rdquo; &mdash; the import is refused deliberately, because pasting
          an article about thirty recipes does not help. Press <em>Show the recipes</em> instead and
          add the ones you want. When that button is not offered, the list keeps its recipes
          somewhere this cannot follow: open it and import the one you want.
        </p>

        <h3>A receipt line matched to the wrong item</h3>
        <p>
          Fix it on the review screen before you confirm: tap the line and choose the right item.
          You only have to do it once. The correction is stored as that store&rsquo;s shorthand for
          that product, and it overrules whatever the shorthand said before, so the same line on
          next month&rsquo;s receipt matches correctly on its own. If a wrong shorthand has already
          been learned, it can also be deleted from the item&rsquo;s page under{' '}
          <strong>Receipt shorthand</strong>.
        </p>

        <h3>A photo scan that failed</h3>
        <p>
          The allowance is checked <em>before</em> the model is asked anything, and a scan is
          recorded only <em>after</em> the model has answered with something readable. So a scan
          refused because the cap is spent, an upload that never arrived, a request that timed
          out, or an image the model could not read structured data from, all cost nothing.
        </p>
        <p>
          The line is drawn at the model answering, not at the scan finishing: in the rare case
          where the reading succeeds and saving the draft afterwards fails, that read has already
          been counted. Recording is also deliberately best-effort in the other direction &mdash;
          if the ledger write itself fails, you keep your result and the scan goes uncounted,
          because losing a row is a smaller problem than turning a good scan into an error.
        </p>

        <h3>The photo buttons are disabled and the allowance is not spent</h3>
        <p>
          Then this deployment has no model key configured, and the Scan tab says so.{' '}
          <strong>Settings &rarr; Status</strong> shows <em>no API key</em> beside Photo analysis.
          Barcode scanning, CSV import, and adding things by hand all still work.
        </p>

        <h3>Recipe search says it is unavailable</h3>
        <p>
          Two different things wear that message, and it says which: either search is not set up
          on this deployment, or the search provider is refusing requests for now. Neither is
          worth retrying blindly. You can still import any recipe by its link &mdash; and when
          search is simply not configured, the <em>From the web</em> section of Cook now offers
          your own source sites as links, to search by hand and import what you like.
        </p>

        <hr />
        <nav className="landing-links">
          <Link to="/">Home</Link>
          {/* A plain anchor: the guides are prerendered files rather than client
              routes, so the router has nothing to match and would answer with
              the app's catch-all. */}
          <a href="/guides/">Guides</a>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </nav>
      </article>
    </div>
  );
}
